import type { AgentRuntimeInteractionRequest, AgentType } from "@cord/shared"
import {
  deriveCodeServerStateFromStatusPayload,
  normalizeThreadStatusPayload,
  type ThreadControlPlaneState,
} from "@/lib/thread-sync-control-plane"
import {
  applyPendingMessageCountSideEffects,
  applyPrFlowSnapshotSideEffects,
  clearResolveConflictSideEffects,
  clearOptimisticWorkRequestPendingSideEffects,
  applyResolveConflictSideEffects,
  applyThreadStatusSideEffects,
  applyYoloFlowSideEffects,
  computeYoloFlowUpdate,
  getLatestPendingQueueTimestamp,
  normalizePendingQueueRecords,
  reconcileLocalOutboxWithServerPending,
} from "@/lib/thread-sync-side-channel-effects"
import type {
  LocalOutboxQueueRecord,
  PendingQueueRecord,
} from "@/lib/session-queue-ui-state"
import type { CodeServerStatus } from "@/hooks/thread-sync-client-types"

export interface ThreadSyncEffectsStateLike {
  codeServerStatus: CodeServerStatus
  codeServerError: string | null
}

export interface ThreadSyncQueueStateLike {
  serverPendingMessages: PendingQueueRecord[]
  serverPendingSessionActions: PendingQueueRecord[]
  localOutbox: LocalOutboxQueueRecord[]
  pendingLocalDispatches?: Map<
    string,
    { message: { type: string; [key: string]: unknown }; lastAttemptedConnectionVersion: number | null }
  >
}

export function applyStatusPayloadToThreadState(args: {
  sessionId: string
  payload: unknown
  controlPlane: ThreadControlPlaneState
  effects: ThreadSyncEffectsStateLike
  queue: ThreadSyncQueueStateLike
  preserveClientMessageIds?: Iterable<string>
  existingInteractions?: AgentRuntimeInteractionRequest[]
  nowMs: number
}): {
  controlPlane: ThreadControlPlaneState
  controlPlaneSnapshotAccepted: boolean
  threadStatusSnapshotAccepted: boolean
  knownAgentType: AgentType | null
} {
  const normalized = normalizeThreadStatusPayload({
    payload: args.payload,
    previousControlPlane: args.controlPlane,
    existingInteractions: args.existingInteractions,
    nowMs: args.nowMs,
  })

  let nextControlPlane = normalized.controlPlane

  const nextYoloFlow = computeYoloFlowUpdate({
    currentUpdatedAt: nextControlPlane.yoloFlowUpdatedAt,
    currentStep: nextControlPlane.yoloFlowStep,
    currentError: nextControlPlane.yoloFlowError,
    step: normalized.yoloFlow.step,
    error: normalized.yoloFlow.error,
    timestamp: normalized.yoloFlow.timestamp,
    mergeFailureKind: normalized.yoloFlow.mergeFailureKind,
    treatNonPositiveTimestampAsMissing: true,
  })

  if (nextYoloFlow.applied) {
    nextControlPlane = {
      ...nextControlPlane,
      yoloFlowStep: nextYoloFlow.nextStep,
      yoloFlowError: nextYoloFlow.nextError,
      yoloFlowUpdatedAt: nextYoloFlow.nextUpdatedAt,
    }

    applyYoloFlowSideEffects({
      sessionId: args.sessionId,
      step: nextYoloFlow.nextStep,
      error: nextYoloFlow.nextError,
      timestamp: nextYoloFlow.nextUpdatedAt,
      mergeFailureKind: nextYoloFlow.mergeFailureKind,
    })
  }

  const codeServerState = deriveCodeServerStateFromStatusPayload({
    currentCodeServerStatus: args.effects.codeServerStatus,
    controlPlaneVscodeUrl: nextControlPlane.vscodeUrl,
  })
  args.effects.codeServerStatus = codeServerState.codeServerStatus
  if (codeServerState.shouldClearError) {
    args.effects.codeServerError = null
  }

  const threadStatusResult = applyThreadStatusSideEffects({
    sessionId: args.sessionId,
    threadStatus: normalized.threadStatus,
    // Only trust the server-emitted status freshness token here. Falling back
    // to browser arrival time mixes clocks with locally-applied activity deltas
    // and can make stale SessionDO status snapshots look newer than they are.
    observedAt: normalized.threadStatus.statusObservedAt ?? undefined,
  })

  if (threadStatusResult.snapshotAccepted) {
    args.queue.serverPendingMessages = normalizePendingQueueRecords(normalized.pendingMessagesRaw)
    args.queue.serverPendingSessionActions = normalizePendingQueueRecords(
      normalized.pendingSessionActionsRaw,
    )
    const serverPendingRecords = [
      ...args.queue.serverPendingMessages,
      ...args.queue.serverPendingSessionActions,
    ]
    args.queue.localOutbox = reconcileLocalOutboxWithServerPending({
      localOutbox: args.queue.localOutbox,
      serverPendingMessages: serverPendingRecords,
      preserveClientMessageIds:
        args.preserveClientMessageIds ??
        args.queue.pendingLocalDispatches?.keys(),
    })
    const serverPendingClientMessageIds = new Set(
      serverPendingRecords
        .map((message) => message.clientMessageId)
        .filter((clientMessageId): clientMessageId is string => typeof clientMessageId === "string"),
    )
    const unresolvedLocalOutboxCount = args.queue.localOutbox.filter((message) => {
      if (serverPendingClientMessageIds.has(message.clientMessageId)) return false
      // Rendered handoff rows are temporary visual mirrors after the runtime
      // echoed the prompt. Counting them here would re-open the sidebar spinner
      // while the canonical transcript row is merely catching up.
      if (typeof message.renderedHandoffExpiresAt === "number") return false
      return true
    }).length
    const pendingMessageCount = serverPendingRecords.length + unresolvedLocalOutboxCount

    applyPendingMessageCountSideEffects({
      sessionId: args.sessionId,
      pendingMessageCount,
      // Local outbox timestamps are browser-clock values while server pending
      // rows and terminal finishes are SessionDO-clock values. When unresolved
      // local work exists, pass null so derived sidebar state treats it as fresh
      // requested work without comparing mixed clocks.
      latestPendingWorkTimestamp: unresolvedLocalOutboxCount > 0
        ? null
        : getLatestPendingQueueTimestamp({
            pendingQueueRecords: serverPendingRecords,
          }),
    })

    if (pendingMessageCount === 0) {
      clearOptimisticWorkRequestPendingSideEffects({
        sessionId: args.sessionId,
      })
    }
  }

  applyPrFlowSnapshotSideEffects({
    sessionId: args.sessionId,
    rawSnapshot: normalized.prFlowSnapshot,
  })

  if (normalized.resolveConflict) {
    applyResolveConflictSideEffects({
      sessionId: args.sessionId,
      requestId: normalized.resolveConflict.requestId,
      conversationId: normalized.resolveConflict.conversationId,
      state: normalized.resolveConflict.state,
      baseBranch: normalized.resolveConflict.baseBranch,
      dispatchMode: normalized.resolveConflict.dispatchMode,
      error: normalized.resolveConflict.error,
      timestamp: normalized.resolveConflict.timestamp,
    })
  } else if (normalized.resolveConflictObservedAt !== null) {
    clearResolveConflictSideEffects({
      sessionId: args.sessionId,
      conversationId: normalized.resolveConflictObservedConversationId,
      timestamp: normalized.resolveConflictObservedAt,
      observedAt: args.nowMs,
    })
  }

  return {
    controlPlane: nextControlPlane,
    controlPlaneSnapshotAccepted: normalized.controlPlaneSnapshotAccepted,
    threadStatusSnapshotAccepted: threadStatusResult.snapshotAccepted,
    knownAgentType: normalized.knownAgentType,
  }
}
